import { useRef, useState, type FormEvent } from "react";
import { ApiError } from "@/core/api/apiTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useOperationLifetime } from "@/shared/hooks/useOperationLifetime";
import { FormNameSchema, type CreateFormRequest, type Form } from "../types/formTypes";
import { FormMetadata } from "./FormMetadata";

interface FormEditorProps {
  businessId: string;
  businessName: string;
  initialForm?: Form;
  onSave: (request: CreateFormRequest, expectedVersion?: number) => Promise<Form>;
  onReload?: () => Promise<Form>;
}

export function FormEditor({ businessId, businessName, initialForm, onSave, onReload }: FormEditorProps) {
  const [snapshot, setSnapshot] = useState(initialForm);
  const [name, setName] = useState(initialForm?.name ?? "");
  const [definition, setDefinition] = useState(initialForm?.definition ?? "{}");
  const [nameError, setNameError] = useState("");
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState(false);
  const [reloading, setReloading] = useState(false);
  const submitting = useRef(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const captureOperation = useOperationLifetime();
  const { goBack, goToFormDetails } = useAppNavigation();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current || conflict) return;
    const parsed = FormNameSchema.safeParse(name);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0].message);
      nameRef.current?.focus();
      return;
    }
    submitting.current = true;
    const isCurrent = captureOperation();
    setPending(true);
    setNameError("");
    setError("");
    try {
      const saved = await onSave({ name: parsed.data, definition }, snapshot?.version);
      if (isCurrent()) goToFormDetails(businessId, saved.id, true);
    } catch (cause) {
      if (!isCurrent()) return;
      if (cause instanceof ApiError && cause.Status === 409) setConflict(true);
      else setError(cause instanceof Error ? cause.message : "Unable to save this form.");
    } finally {
      submitting.current = false;
      if (isCurrent()) setPending(false);
    }
  }

  async function reloadLatest() {
    if (!onReload || submitting.current) return;
    submitting.current = true;
    const isCurrent = captureOperation();
    setReloading(true);
    setError("");
    try {
      const latest = await onReload();
      if (!isCurrent()) return;
      setSnapshot(latest);
      setName(latest.name);
      setDefinition(latest.definition);
      setNameError("");
      setConflict(false);
    } catch (cause) {
      if (isCurrent()) setError(cause instanceof Error ? cause.message : "Unable to reload the latest version.");
    } finally {
      submitting.current = false;
      if (isCurrent()) setReloading(false);
    }
  }

  return (
    <FormPage
      header={<PageHeader title={initialForm ? "Edit form" : "Create form"} description={businessName}
        actions={<PageHeaderAction onClick={goBack}>Back</PageHeaderAction>} />}
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outline" onClick={goBack}>Cancel</Button>
        <Button type="submit" disabled={pending || reloading || conflict}>{pending ? "Saving..." : initialForm ? "Save new version" : "Create form"}</Button>
      </>}
    >
      {error && <ErrorState message={error} />}
      {conflict && (
        <Section title="A newer version is available" card>
          <p role="alert" className="text-sm text-muted-foreground">Another member saved this form. Your draft has not been changed. Copy anything you want to keep before reloading; reloading replaces your draft with the latest saved version.</p>
          <Button variant="outline" disabled={reloading} onClick={() => void reloadLatest()}>{reloading ? "Reloading..." : "Reload latest version"}</Button>
        </Section>
      )}
      <Section title="Form content">
        <fieldset disabled={pending || reloading} className="min-w-0 space-y-[18px]">
          <FormField id="form-name" label="Name" required error={nameError} description="1–100 characters. Surrounding spaces are removed.">
            <Input ref={nameRef} name="name" value={name} required onChange={event => { setName(event.target.value); setNameError(""); }} />
          </FormField>
          <FormField id="form-definition" label="Raw definition" description="Stored exactly as entered. JSON syntax is not checked; empty text is allowed.">
            <TextArea name="definition" value={definition} onChange={event => setDefinition(event.target.value)} rows={14} spellCheck={false} className="font-mono" />
          </FormField>
        </fieldset>
        <p className="text-sm text-muted-foreground">{snapshot
          ? `Editing version ${snapshot.version}. Every save creates a new version, even without changes.`
          : "Your new form will start at version 1."}</p>
      </Section>
      {snapshot && <FormMetadata form={snapshot} />}
    </FormPage>
  );
}
